import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { useLocalData } from "../hooks/useLocalData";
import {
  Home, Users, Mic, ClipboardList, Settings,
  AlertTriangle, WifiOff, CheckCircle, Plus,
  X, BarChart3, MapPin, Clock, FileText, Shield,
} from "lucide-react";
import BottomNav from "../components/BottomNav";
import RecoveryBanner from "../components/RecoveryBanner";

export default function DashboardPage() {
  const { profile } = useAuth();
  const { isOnline } = useOnlineStatus();
  const {
    individuals, appointments, completedSessions, notes,
    addAppointment, deleteAppointment, getTodayAppointments, getAnalytics,
  } = useLocalData();
  const navigate = useNavigate();

  const [showAddVisit, setShowAddVisit] = useState(false);
  const [newVisit, setNewVisit] = useState({
    individualId: "",
    date: new Date().toLocaleDateString(),
    time: "",
    locationType: "Out-of-Clinic",
    location: "",
    serviceType: "ICM Weekly Visit",
  });

  const todayAppts = getTodayAppointments();
  const analytics = getAnalytics();

  const todayCompleted = todayAppts.filter((a) => a.status === "completed").length;
  const draftNotes = notes.filter((n) => n.status === "draft");
  const weekTotal = analytics?.week?.sessions || 0;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const handleAddVisit = () => {
    if (!newVisit.individualId || !newVisit.time) return;
    const ind = individuals.find((i) => i.id === newVisit.individualId);
    addAppointment({
      ...newVisit,
      individualName: ind ? `${ind.firstName} ${ind.lastName}` : "Unknown",
      address: newVisit.location || ind?.address || "",
    });
    setShowAddVisit(false);
    setNewVisit({
      individualId: "",
      date: new Date().toLocaleDateString(),
      time: "",
      locationType: "Out-of-Clinic",
      location: "",
      serviceType: "ICM Weekly Visit",
    });
  };

  const handleStartSession = (appt) => {
    const ind = individuals.find((i) => i.id === appt.individualId);
    navigate("/session/new/" + appt.individualId, {
      state: {
        appointmentId: appt.id,
        individualName: appt.individualName,
        individualId: appt.individualId,
        dob: ind?.dob || "",
        locationType: appt.locationType,
        location: appt.address || appt.location,
        serviceType: appt.serviceType,
        knownMedications: ind?.medications || [],
        knownDiagnoses: ind?.diagnoses || [],
      },
    });
  };

  return (
    <div className="min-h-screen bg-surface-primary">
      {/* Header */}
      <header
        className="sticky top-0 z-50 px-5 py-4 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, #0f1729, #162033)",
          borderBottom: "1px solid rgba(99,179,237,0.1)",
        }}
      >
        <div>
          <div className="text-xs text-gray-500">{getGreeting()},</div>
          <div className="text-xl font-bold text-gray-100">
            {profile?.displayName || "there"}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(183,148,244,0.08)", border: "1px solid rgba(183,148,244,0.15)" }}
            onClick={() => navigate("/admin")}
            title="Supervisor Dashboard"
          >
            <Shield size={16} className="text-purple-400" />
          </button>
          <button
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(79,209,197,0.1)", border: "1px solid rgba(79,209,197,0.2)" }}
            onClick={() => navigate("/analytics")}
          >
            <BarChart3 size={16} className="text-teal-400" />
          </button>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
            style={{
              background: isOnline ? "rgba(72,187,120,0.1)" : "rgba(252,129,129,0.1)",
              color: isOnline ? "#68d391" : "#fc8181",
            }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: isOnline ? "#48bb78" : "#fc8181" }}
            />
            {isOnline ? "Online" : "Offline"}
          </div>
        </div>
      </header>

      <RecoveryBanner />

      <div className="px-5 py-5 pb-28 stagger-children">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2.5 mb-6">
          {[
            { label: "Today's Visits", value: todayAppts.length, color: "#63b3ed" },
            { label: "Completed", value: todayCompleted, color: "#48bb78" },
            { label: "This Week", value: weekTotal, color: "#4fd1c5" },
          ].map((s, i) => (
            <div key={i} className="glass-card text-center py-4 px-3">
              <div className="text-3xl font-extrabold leading-none" style={{ color: s.color }}>
                {s.value}
              </div>
              <div className="text-[10px] text-gray-500 mt-1.5 font-semibold tracking-wider uppercase">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Time Saved Banner */}
        {analytics && analytics.week.timeSavedMinutes > 0 && (
          <div
            className="glass-card p-4 mb-3"
            style={{ borderColor: "rgba(79,209,197,0.15)", background: "rgba(79,209,197,0.04)" }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock size={14} className="text-teal-400" />
              <span className="text-[11px] font-bold text-teal-400 tracking-wider uppercase">
                Time Saved This Week
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-teal-400">
                {analytics.week.timeSavedMinutes >= 60
                  ? `${Math.floor(analytics.week.timeSavedMinutes / 60)}h ${analytics.week.timeSavedMinutes % 60}m`
                  : `${analytics.week.timeSavedMinutes}m`
                }
              </span>
              <span className="text-xs text-gray-500">vs manual documentation</span>
            </div>
          </div>
        )}

        {/* Offline Banner */}
        {!isOnline && (
          <div
            className="glass-card p-4 mb-3 flex items-center gap-3"
            style={{ borderColor: "rgba(236,201,75,0.12)", background: "rgba(236,201,75,0.03)" }}
          >
            <WifiOff size={18} className="text-yellow-400 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-yellow-400">Offline Mode</div>
              <div className="text-[11px] text-gray-500 mt-0.5">Sessions will sync when connected</div>
            </div>
          </div>
        )}

        {/* Today's Schedule */}
        <div className="flex items-center justify-between mt-5 mb-2">
          <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase">
            Today's Schedule
          </div>
          <button
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{
              background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
              color: "white",
            }}
            onClick={() => setShowAddVisit(true)}
          >
            <Plus size={12} /> Add Visit
          </button>
        </div>

        {todayAppts.length === 0 && (
          <div className="glass-card p-6 text-center mb-3">
            <div className="text-sm text-gray-500 mb-3">No visits scheduled for today</div>
            <button
              className="px-4 py-2 rounded-xl text-sm font-semibold"
              style={{ background: "rgba(99,179,237,0.1)", color: "#63b3ed", border: "1px solid rgba(99,179,237,0.2)" }}
              onClick={() => setShowAddVisit(true)}
            >
              Schedule a Visit
            </button>
          </div>
        )}

        {todayAppts.map((appt) => (
          <div
            key={appt.id}
            className="glass-card p-4 mb-3"
            style={{
              opacity: appt.status === "completed" ? 0.5 : 1,
              borderLeft: appt.status === "completed" ? "3px solid #48bb78" : "3px solid #63b3ed",
              cursor: appt.status === "completed" ? "default" : "pointer",
            }}
            onClick={() => {
              if (appt.status !== "completed") handleStartSession(appt);
            }}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs text-gray-500 font-semibold">{appt.time}</div>
                <div className="text-base font-bold text-gray-100 mt-0.5">{appt.individualName}</div>
                <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <MapPin size={10} /> {appt.locationType} — {appt.address || appt.location || "TBD"}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {appt.status === "completed" ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-green-500/10 text-green-400">
                    <CheckCircle size={10} /> Done
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-400/10 text-blue-400">
                    Tap to Start
                  </span>
                )}
                <span className="text-[11px] text-gray-500">{appt.serviceType}</span>
              </div>
            </div>
          </div>
        ))}

        {/* Pending Drafts */}
        {draftNotes.length > 0 && (
          <>
            <div className="text-[11px] font-bold text-yellow-400 tracking-[1.5px] uppercase mt-5 mb-2">
              Pending Drafts ({draftNotes.length})
            </div>
            {draftNotes.map((draft) => (
              <div
                key={draft.id}
                className="glass-card p-4 mb-3"
                style={{ borderLeft: "3px solid #ecc94b" }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-base font-bold text-gray-100">{draft.individualName}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {draft.date} · {draft.duration} · {draft.serviceType}
                    </div>
                    {draft.hasPriorityFlags && (
                      <span className="inline-flex items-center gap-0.5 mt-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500/15 text-red-400 uppercase">
                        <AlertTriangle size={8} /> Priority flags
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-yellow-500/10 text-yellow-400">
                      <FileText size={10} /> Draft
                    </span>
                  </div>
                </div>
                <button
                  className="w-full mt-3 py-2.5 rounded-xl text-xs font-semibold text-white text-center"
                  style={{ background: "linear-gradient(135deg, #3182ce, #2b6cb0)" }}
                  onClick={() =>
                    navigate("/session/processing", {
                      state: {
                        sessionId: draft.id,
                        draftNoteId: draft.id,
                        transcript: draft.transcript,
                        individualName: draft.individualName,
                        dob: draft.dob,
                        locationType: draft.locationType,
                        location: draft.location,
                        serviceType: draft.serviceType,
                        knownMedications: draft.knownMedications || [],
                        knownDiagnoses: draft.knownDiagnoses || [],
                        extractedFacts: draft.extractedFacts || [],
                        duration: draft.durationSeconds || 1800,
                        sessionData: {
                          duration: draft.durationSeconds || 1800,
                          flaggedSegments: draft.flaggedNotes || [],
                        },
                      },
                    })
                  }
                >
                  Generate Note Now
                </button>
              </div>
            ))}
          </>
        )}

        {/* Quick Actions */}
        <div className="text-[11px] font-bold text-accent-teal tracking-[1.5px] uppercase mt-5 mb-2">
          Quick Actions
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          <div className="glass-card p-4 text-center" onClick={() => navigate("/caseload")}>
            <Users size={22} className="text-gray-300 mx-auto" />
            <div className="text-xs font-semibold mt-2">Caseload</div>
            <div className="text-[10px] text-gray-500 mt-0.5">{individuals.length} individuals</div>
          </div>
          <div className="glass-card p-4 text-center" onClick={() => navigate("/notes")}>
            <ClipboardList size={22} className="text-gray-300 mx-auto" />
            <div className="text-xs font-semibold mt-2">Notes</div>
            <div className="text-[10px] text-gray-500 mt-0.5">View & export</div>
          </div>
          <div className="glass-card p-4 text-center" onClick={() => navigate("/analytics")}>
            <BarChart3 size={22} className="text-gray-300 mx-auto" />
            <div className="text-xs font-semibold mt-2">Analytics</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Time saved</div>
          </div>
        </div>
      </div>

      {/* ===== ADD VISIT MODAL ===== */}
      {showAddVisit && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 px-4 pb-4">
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden animate-slide-up"
            style={{
              background: "linear-gradient(135deg, #0f1729, #162033)",
              border: "1px solid rgba(99,179,237,0.15)",
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between p-5 pb-3">
              <h3 className="text-lg font-bold text-gray-100">Add Visit</h3>
              <button onClick={() => setShowAddVisit(false)}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-3">
              {/* Select Individual */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Individual</label>
                <select
                  className="field-input text-sm"
                  style={{ appearance: "auto" }}
                  value={newVisit.individualId}
                  onChange={(e) => {
                    const ind = individuals.find((i) => i.id === e.target.value);
                    setNewVisit({
                      ...newVisit,
                      individualId: e.target.value,
                      location: ind?.address || "",
                    });
                  }}
                >
                  <option value="">Select individual...</option>
                  {individuals.map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.firstName} {ind.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Time</label>
                <input
                  type="time"
                  className="field-input text-sm"
                  value={newVisit.time}
                  onChange={(e) => setNewVisit({ ...newVisit, time: e.target.value })}
                />
              </div>

              {/* Date */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Date</label>
                <input
                  type="date"
                  className="field-input text-sm"
                  value={new Date().toISOString().split("T")[0]}
                  onChange={(e) => {
                    const d = new Date(e.target.value + "T12:00:00");
                    setNewVisit({ ...newVisit, date: d.toLocaleDateString() });
                  }}
                />
              </div>

              {/* Location Type */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Location Type</label>
                <div className="flex gap-2">
                  {["In-Clinic", "Out-of-Clinic"].map((type) => (
                    <button
                      key={type}
                      className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-center"
                      style={{
                        background: newVisit.locationType === type ? "rgba(49,130,206,0.2)" : "rgba(15,23,42,0.5)",
                        color: newVisit.locationType === type ? "#63b3ed" : "#718096",
                        border: `1px solid ${newVisit.locationType === type ? "rgba(49,130,206,0.3)" : "rgba(99,179,237,0.06)"}`,
                      }}
                      onClick={() => setNewVisit({ ...newVisit, locationType: type })}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Address */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Location / Address</label>
                <input
                  className="field-input text-sm"
                  placeholder="e.g. Ind Residence, community center..."
                  value={newVisit.location}
                  onChange={(e) => setNewVisit({ ...newVisit, location: e.target.value })}
                />
              </div>

              {/* Service Type */}
              <div>
                <label className="text-xs text-gray-500 font-semibold block mb-1">Service Type</label>
                <select
                  className="field-input text-sm"
                  style={{ appearance: "auto" }}
                  value={newVisit.serviceType}
                  onChange={(e) => setNewVisit({ ...newVisit, serviceType: e.target.value })}
                >
                  <option>ICM Weekly Visit</option>
                  <option>ICM First Meeting</option>
                  <option>ICM Follow-up</option>
                  <option>PSR Session</option>
                  <option>Crisis Intervention</option>
                  <option>Peer Support</option>
                </select>
              </div>

              <button
                className="w-full py-3.5 rounded-xl text-white font-semibold text-sm mt-2"
                style={{
                  background: newVisit.individualId && newVisit.time
                    ? "linear-gradient(135deg, #3182ce, #2b6cb0)"
                    : "rgba(49,130,206,0.3)",
                  opacity: newVisit.individualId && newVisit.time ? 1 : 0.5,
                }}
                onClick={handleAddVisit}
                disabled={!newVisit.individualId || !newVisit.time}
              >
                Add to Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav active="home" />
    </div>
  );
}